import clsx from 'clsx';
import type React from 'react';
import type { ComponentProps } from 'react';
import type { PrefetchMode } from '@//hooks/useLinkPrefetch';
import { AppLink } from '../AppLink';
import styles from './TextButton.module.scss';

type NextLinkPropsSubset = Pick<ComponentProps<typeof AppLink>, 'replace' | 'scroll' | 'locale'>;

interface SharedProps {
  icon: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'no-bg' | 'brand' | 'critical';
  iconPosition?: 'start' | 'end';
  size?: 'sm' | 'md';
  children?: React.ReactNode;
  className?: string;
  testId?: string;
  noShrink?: boolean;
}

type TextButtonProps = SharedProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: undefined; // Disallow href for button
  };

type LinkProps = SharedProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> &
  NextLinkPropsSubset & {
    href: string;
    /** Prefetch policy. Default 'none' — explicit opt-in required. */
    prefetch?: PrefetchMode;
  };

type Props = TextButtonProps | LinkProps;

export const TextButton: React.FC<Props> = (props) => {
  const {
    icon,
    variant = 'primary',
    iconPosition = 'end',
    size = 'md',
    children,
    className,
    testId,
    noShrink,
    ...rest
  } = props;

  const classNames = clsx(styles.button, className, {
    [styles.variantPrimary]: variant === 'primary',
    [styles.variantSecondary]: variant === 'secondary',
    [styles.variantNoBg]: variant === 'no-bg',
    [styles.variantBrand]: variant === 'brand',
    [styles.variantCritical]: variant === 'critical',
    [styles.sizeSm]: size === 'sm',
    [styles.sizeMd]: size === 'md',
    [styles.iconStart]: iconPosition === 'start',
    [styles.noShrink]: noShrink,
  });

  const content = (
    <>
      {children}
      <span className={styles.iconBackdrop} aria-hidden="true">
        {icon}
      </span>
    </>
  );

  if ('href' in props && props.href) {
    const { href, replace, scroll, locale, prefetch, ...anchorProps } = rest as LinkProps;
    return (
      <AppLink
        href={href}
        replace={replace}
        scroll={scroll}
        locale={locale}
        prefetch={prefetch}
        className={classNames}
        data-testid={testId}
        {...anchorProps}
      >
        {content}
      </AppLink>
    );
  }

  const { onClick, ...buttonProps } = rest as TextButtonProps;
  return (
    <button
      type={buttonProps.type || 'button'}
      onClick={onClick}
      className={classNames}
      data-testid={testId}
      {...buttonProps}
    >
      {content}
    </button>
  );
};
